// Countries blocked from accessing the site.
// Add ISO-3166-1 alpha-2 codes (uppercase).
export const BLOCKED_COUNTRIES: ReadonlySet<string> = new Set(["KR", "JP"]);

const STORAGE_KEY = "rm_geo_country";
const STORAGE_TS_KEY = "rm_geo_country_ts";
const CACHE_MS = 24 * 60 * 60 * 1000; // 24h

/**
 * Detects the visitor's country code via Cloudflare's cdn-cgi/trace.
 * Returns a 2-letter uppercase ISO country code, or null if detection failed.
 * Result is cached in sessionStorage for 24h to avoid repeated lookups.
 */
export async function detectCountry(): Promise<string | null> {
  try {
    const cached = sessionStorage.getItem(STORAGE_KEY);
    const cachedTs = Number(sessionStorage.getItem(STORAGE_TS_KEY) || 0);
    if (cached && Date.now() - cachedTs < CACHE_MS) {
      return cached;
    }
  } catch {
    // sessionStorage unavailable; continue to fetch
  }

  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 3000);
    const res = await fetch("https://www.cloudflare.com/cdn-cgi/trace", {
      signal: controller.signal,
      cache: "no-store",
    });
    clearTimeout(timeout);
    if (!res.ok) return null;
    const text = await res.text();
    const match = text.match(/^loc=([A-Z]{2})$/m);
    const country = match?.[1] ?? null;
    if (country) {
      try {
        sessionStorage.setItem(STORAGE_KEY, country);
        sessionStorage.setItem(STORAGE_TS_KEY, String(Date.now()));
      } catch {
        // ignore storage errors
      }
    }
    return country;
  } catch {
    return null;
  }
}

export function isBlockedCountry(country: string | null): boolean {
  if (!country) return false;
  return BLOCKED_COUNTRIES.has(country.toUpperCase());
}
